import { motion } from "framer-motion";
import { ArrowUpRight, HelpCircle, Gamepad2, Users2, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import SEO, { orgSchema, breadcrumbSchema, faqSchema } from "@/components/SEO";
import { PageHero, SectionHeading, FinalCTA } from "@/components/sections";
import { Reveal, stagger, item } from "@/components/motion";
import Icon from "@/components/Icon";
import { useAppointment } from "@/components/AppointmentContext";
import {
  PED_IMAGES, PED_CONDITIONS, PED_PARENT_Qs, PED_AGES, PED_EXPERTISE, PED_FAQS,
} from "@/data/pediatric";

export default function Pediatric() {
  const { openDialog } = useAppointment();
  const crumbs = [{ name: "Home", path: "/" }, { name: "Pediatric Psychotherapy", path: "/pediatric-psychotherapy" }];

  return (
    <>
      <SEO
        title="Pediatric Psychotherapy — Child & Teen CBT Therapist in Connecticut"
        description="Specialized pediatric psychotherapy for children and teens in Connecticut. Evidence-based CBT for anxiety, ADHD, behavioral challenges, OCD, depression, emotional regulation, school issues, and more."
        path="/pediatric-psychotherapy" image={PED_IMAGES.hero}
        jsonLd={[orgSchema, breadcrumbSchema(crumbs), faqSchema(PED_FAQS)]} />

      {/* HERO */}
      <PageHero eyebrow="Pediatric Psychotherapy · Connecticut" breadcrumb={crumbs}
        title="Helping Children, Teens & Families Thrive"
        intro="Specialized Cognitive Behavioral Therapy (CBT) for children and adolescents facing emotional, behavioral, and school-related challenges. Now accepting new clients."
        image={PED_IMAGES.hero} />
      <section className="border-b border-navy-100 bg-white">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-4 px-6 py-6 lg:px-12">
          <Button onClick={openDialog} data-testid="ped-hero-schedule"
            className="rounded-full bg-teal hover:bg-teal-dark text-white h-13 px-7 hover:-translate-y-0.5 transition-transform">
            Schedule a Consultation <ArrowUpRight className="ml-1 h-4 w-4" />
          </Button>
          <span className="text-sm text-muted-foreground">Warm, evidence-based care · Telehealth &amp; in-person in Southington, CT</span>
        </div>
      </section>

      {/* CONDITIONS TREATED */}
      <section className="py-24 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <SectionHeading center eyebrow="Conditions We Treat"
            title="Support for the challenges kids and teens face."
            intro="Evidence-based CBT tailored to your child's age, personality, and goals." />
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="mt-14 grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {PED_CONDITIONS.map((c) => (
              <motion.div key={c.name} variants={item}
                className="group flex items-center gap-4 rounded-2xl border border-navy-100 bg-white p-5 shadow-soft transition-all hover:-translate-y-1 hover:border-teal/30 hover:shadow-premium"
                data-testid="ped-condition-card">
                <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-softblue/60 text-teal transition-colors group-hover:bg-teal group-hover:text-white">
                  <Icon name={c.icon} className="h-6 w-6" />
                </span>
                <span className="font-medium text-navy leading-tight">{c.name}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* HOW PEDIATRIC THERAPY WORKS */}
      <section className="bg-secondary py-24 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <div className="grid gap-14 lg:grid-cols-2 lg:items-center">
            <Reveal>
              <div className="relative">
                <div className="pointer-events-none absolute -inset-4 rounded-[2rem] bg-gradient-to-br from-softblue to-transparent blur-2xl opacity-70" />
                <img src={PED_IMAGES.play} alt="A child engaged in play-based therapy activities"
                  className="relative aspect-[4/3] w-full rounded-[2rem] object-cover shadow-premium" />
              </div>
            </Reveal>
            <div>
              <SectionHeading eyebrow="The Approach" title="How Pediatric Therapy Works" />
              <div className="mt-6 space-y-5 text-lg text-muted-foreground leading-relaxed">
                <p><span className="font-semibold text-navy">Therapy isn't just talking.</span> Children learn best through structured Cognitive Behavioral Therapy (CBT), interactive activities, games, skill-building exercises, and parent coaching.</p>
                <p>Sessions are designed to be engaging, age-appropriate, and focused on helping children build practical coping skills they can use at home, at school, and in everyday life.</p>
                <p>Parents are active partners throughout treatment, ensuring progress continues beyond each therapy session.</p>
              </div>
              <div className="mt-8 grid gap-4 sm:grid-cols-3">
                {[{ i: Gamepad2, t: "Engaging & playful" }, { i: Home, t: "Skills for real life" }, { i: Users2, t: "Parents as partners" }].map((b) => (
                  <div key={b.t} className="rounded-2xl border border-navy-100 bg-white p-4 text-center shadow-soft">
                    <b.i className="mx-auto h-6 w-6 text-teal" />
                    <div className="mt-2 text-sm font-medium text-navy">{b.t}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* IS YOUR CHILD STRUGGLING WITH... */}
      <section className="py-24 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <SectionHeading center eyebrow="For Parents" title="Is Your Child Struggling With…"
            intro="If any of these sound familiar, you're not alone — and support is available." />
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {PED_PARENT_Qs.map((q, i) => (
              <motion.div key={i} variants={item}
                className="rounded-3xl border border-navy-100 bg-gradient-to-br from-white to-softblue/30 p-6 shadow-soft"
                data-testid="ped-parent-q">
                <HelpCircle className="h-7 w-7 text-teal" />
                <p className="mt-4 font-display text-lg text-navy leading-snug">{q}</p>
              </motion.div>
            ))}
          </motion.div>
          <Reveal delay={0.15}>
            <div className="mt-10 text-center">
              <Button onClick={openDialog} className="rounded-full bg-teal hover:bg-teal-dark text-white h-13 px-8 hover:-translate-y-0.5 transition-transform">
                Schedule a Consultation
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* AGE-SPECIFIC */}
      <section className="bg-secondary py-24 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <SectionHeading center eyebrow="Care by Age" title="Age-appropriate therapy for every stage." />
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="mt-14 grid gap-6 md:grid-cols-3">
            {PED_AGES.map((a) => (
              <motion.div key={a.range} variants={item}
                className="overflow-hidden rounded-3xl border border-navy-100 bg-white shadow-soft" data-testid="ped-age-card">
                <img src={PED_IMAGES[a.image]} alt={`Therapy for ${a.range}`} className="aspect-[16/10] w-full object-cover" />
                <div className="p-7">
                  <span className="grid h-12 w-12 place-items-center rounded-2xl bg-softblue/60 text-teal"><Icon name={a.icon} className="h-6 w-6" /></span>
                  <h3 className="mt-4 font-display text-2xl text-navy">{a.range}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{a.focus}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* SPECIALIZED EXPERTISE */}
      <section className="py-24 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <SectionHeading center eyebrow="Why Beacon" title="Specialized Expertise"
            intro="Deep, practical experience supporting children, teens, and the families around them." />
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {PED_EXPERTISE.map((e) => (
              <motion.div key={e.name} variants={item}
                className="flex items-center gap-4 rounded-2xl border border-navy-100 bg-white p-5 shadow-soft transition-transform hover:-translate-y-1">
                <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-teal/10 text-teal"><Icon name={e.icon} className="h-6 w-6" /></span>
                <span className="font-medium text-navy">{e.name}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-secondary py-24 lg:py-28">
        <div className="mx-auto max-w-3xl px-6 lg:px-12">
          <SectionHeading center eyebrow="For Parents" title="Frequently Asked Questions for Parents" />
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="mt-12" data-testid="ped-faq">
              {PED_FAQS.map((f, i) => (
                <AccordionItem key={i} value={`p-${i}`} className="border-navy-100">
                  <AccordionTrigger className="text-left font-display text-lg text-navy hover:text-teal hover:no-underline">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-base text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-10 rounded-3xl bg-navy p-8 text-center sm:p-10">
              <h3 className="font-display text-2xl text-white sm:text-3xl">Still Have Questions?</h3>
              <p className="mx-auto mt-3 max-w-xl text-white/70">Choosing a therapist for your child is an important decision, and it's natural to have questions. I'm happy to answer them and help you determine the next best step for your family.</p>
              <Button onClick={openDialog} className="mt-6 rounded-full bg-teal hover:bg-teal-dark text-white h-13 px-8">Schedule a Consultation</Button>
            </div>
          </Reveal>
        </div>
      </section>

      <FinalCTA title="Ready to help your child thrive?" text="Take the first step today. Schedule a warm, no-pressure consultation to see how pediatric CBT can help your child feel more confident, calm, and capable." />
    </>
  );
}
